import React, { useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ButtonGroup,
  ToggleButton,
  Badge,
  Offcanvas,
  ListGroup,
  Stack,
  Modal,
  Form,
  Spinner,
} from "react-bootstrap";
import { verifyLogin } from "../utilities/authHelper";
import HeaderComponent from "./HeaderComponent";

/**
 * EMAlpha Pricing / Plans Page (Bootstrap + inline styles)
 * - Modern dark theme inspired by TradingView + Databento structure
 * - Tabs: All / Agents / CoTrader
 * - Tier selector for CoTrader FX + MacroMonitor
 * - Sales-only for TradeTracker + CoHedger + Custom Monitor
 * - 30-day free trial for CoTrader + MacroMonitor
 * - Bundle configurator (sticky desktop, offcanvas mobile)
 *
 * Lead Modal:
 * - Opens for Contact Sales + Request Pricing
 * - Prefills bundle where possible
 * - Lets user edit bundle via dropdown + tier selection
 * - Sends POST to https://api.emalpha.com/auth/contact_sales
 */

const TIERS = [
  { key: "basic", label: "Basic", priceMonthly: 499 },
  { key: "mid", label: "Mid", priceMonthly: 799 },
  { key: "premium", label: "Premium", priceMonthly: 999 },
];

const PRODUCTS = [
  {
    id: "macro_monitor",
    group: "monitors",
    tiered: true,
    name: "MacroMonitor",
    subtitle: "Micro Monitor • Intelligence Layer",
    description:
      "Real-time market intelligence with targeted insights and deep research Q&A.",
    designedFor: ["Portfolio Managers", "Researchers", "Data Analysts"],
    capabilities: [
      "Real-time market intelligence",
      "AI-powered customization and targeted insights",
      "AI-powered Q&A for deep dive into research questions",
      "Seamless integration into trading platforms",
    ],
    ctaPrimary: "Add to Bundle",
  },
  {
    id: "cotrader_fx",
    group: "cotrader",
    tiered: true,
    name: "CoTrader FX",
    subtitle: "CoTrader • Execution Layer",
    description:
      "AI-powered FX workflow for analysis, planning, and execution decisions.",
    designedFor: ["Traders", "Portfolio Managers"],
    capabilities: [
      "Up-to-the-second explanation of market price moves",
      "Pre-trade analysis and execution planning",
      "Real-time liquidity analysis",
      "AI-powered Q&A for trade execution and market conditions",
    ],
    ctaPrimary: "Add to Bundle",
  },
  {
    id: "trade_tracker",
    group: "cotrader",
    tiered: false,
    salesOnly: true,
    name: "TradeTracker",
    subtitle: "Micro Monitor • Execution Intelligence",
    description:
      "Trade assessment with market impact context, liquidity metrics, and audit-ready answers.",
    designedFor: ["Traders & Middle Office", "Compliance"],
    capabilities: [
      "Context-aware trade execution assessment",
      "Captures impacts on historical trades due to news and data releases",
      "Combines liquidity metrics with market impact analysis",
      "AI-powered Q&A for trade execution assessment",
    ],
    ctaPrimary: "Contact Sales",
  },
  {
    id: "cohedger",
    group: "cotrader",
    tiered: false,
    salesOnly: true,
    name: "CoHedger",
    subtitle: "Micro Monitor • Hedging Intelligence",
    description:
      "Forward tenor selection, roll optimization, and macro-aware hedging scenarios.",
    designedFor: ["Corporate Treasuries", "FX Traders & Risk Teams"],
    capabilities: [
      "Macro-aware forward tenor selection and roll optimization",
      "Anticipates settlement and roll risks driven by elections, news, and data releases",
      "Integrates liquidity, carry, and macro-event analysis",
      "AI-powered Q&A for forward curves, hedging costs, and roll scenarios",
    ],
    ctaPrimary: "Contact Sales",
  },
  {
    id: "custom_monitor",
    group: "monitors",
    tiered: false,
    salesOnly: true,
    name: "Custom Monitor",
    subtitle: "Custom • Built for your desk",
    description:
      "Custom monitors tailored to your markets, themes, and workflows—powered by EMGPT.",
    designedFor: ["Institutional Teams"],
    capabilities: [
      "Your selected countries / markets",
      "Your selected themes / keywords",
      "EMGPT customized to your persona",
      "Custom pricing & rollout support",
    ],
    ctaPrimary: "Request Sales Call",
  },
];

function usd(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

export default function EmalphaPricingPageModern() {
  const [tab, setTab] = useState("all"); // all | monitors | cotrader
  const [billing, setBilling] = useState("monthly"); // monthly | annual (UI only)
  const [tierByProduct, setTierByProduct] = useState({
    macro_monitor: "basic",
    cotrader_fx: "basic",
  });

  // existing bundle state (UI)
  const [bundle, setBundle] = useState([]); // {productId, tierKey?}
  const [showBundleMobile, setShowBundleMobile] = useState(false);

  // lead modal state
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [salesModalMode, setSalesModalMode] = useState("request_pricing"); // contact_sales | request_pricing
  const [salesForm, setSalesForm] = useState({
    full_name: "",
    work_email: "",
    company_name: "",
    message: "",
  });
  const [selectedBundleDraft, setSelectedBundleDraft] = useState([]); // [{product_id, tier_key?}]
  const [submittingLead, setSubmittingLead] = useState(false);
  const [leadToast, setLeadToast] = useState({ ok: null, msg: "" }); // ok: true/false/null

  const theme = useMemo(
    () => ({
      bg: "#0B1020",
      bg2: "#070A14",
      card: "rgba(255,255,255,0.06)",
      card2: "rgba(255,255,255,0.04)",
      border: "rgba(255,255,255,0.10)",
      text: "rgba(255,255,255,0.92)",
      text2: "rgba(255,255,255,0.72)",
      text3: "rgba(255,255,255,0.55)",
      accent: "#58A6FF",
      accent2: "#7C5CFF",
      ok: "#34D399",
      warning: "#FBBF24",
      shadow: "0 18px 50px rgba(0,0,0,0.45)",
    }),
    [],
  );

  const styles = useMemo(
    () => ({
      page: {
        minHeight: "100vh",
        background: `radial-gradient(1200px 600px at 20% 10%, rgba(124,92,255,0.22), transparent 60%),
                     radial-gradient(900px 500px at 85% 20%, rgba(88,166,255,0.18), transparent 55%),
                     linear-gradient(180deg, ${theme.bg} 0%, ${theme.bg2} 100%)`,
        padding: "34px 0 64px",
      },
      hero: {
        borderRadius: 22,
        border: `1px solid ${theme.border}`,
        background: `linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.03) 100%)`,
        boxShadow: theme.shadow,
        padding: 22,
      },
      title: {
        margin: 0,
        color: theme.text,
        fontSize: 30,
        fontWeight: 900,
        letterSpacing: -0.4,
      },
      subtitle: {
        marginTop: 10,
        color: theme.text2,
        fontSize: 14,
        lineHeight: 1.45,
        maxWidth: 900,
      },
      chipsRow: {
        marginTop: 14,
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
      },
      chip: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.06)",
        border: `1px solid ${theme.border}`,
        color: theme.text2,
        fontSize: 12,
        fontWeight: 800,
      },
      pillAccent: {
        background: "rgba(88,166,255,0.14)",
        border: "1px solid rgba(88,166,255,0.35)",
        color: theme.text,
      },
      tabsWrap: {
        display: "inline-flex",
        background: "rgba(255,255,255,0.06)",
        border: `1px solid ${theme.border}`,
        borderRadius: 14,
        padding: 4,
      },
      tabBtn: (active) => ({
        border: "none",
        borderRadius: 12,
        padding: "8px 14px",
        fontWeight: 900,
        background: active
          ? `linear-gradient(90deg, ${theme.accent} 0%, ${theme.accent2} 100%)`
          : "transparent",
        color: active ? "#0b1020" : theme.text2,
        cursor: "pointer",
        minWidth: 90,
      }),
      toggleWrap: {
        display: "inline-flex",
        background: "rgba(255,255,255,0.06)",
        border: `1px solid ${theme.border}`,
        borderRadius: 14,
        padding: 4,
        gap: 4,
      },
      toggleBtn: (active) => ({
        border: "none",
        borderRadius: 12,
        padding: "8px 12px",
        fontWeight: 900,
        background: active ? "rgba(255,255,255,0.88)" : "transparent",
        color: active ? "#0b1020" : theme.text2,
        cursor: "pointer",
      }),
      sectionTitle: {
        marginTop: 26,
        marginBottom: 10,
        color: theme.text,
        fontSize: 15,
        fontWeight: 900,
        letterSpacing: 0.2,
        textTransform: "uppercase",
      },
      sectionNote: {
        marginTop: -4,
        marginBottom: 14,
        color: theme.text3,
        fontSize: 13,
      },
      card: {
        borderRadius: 22,
        border: `1px solid ${theme.border}`,
        background: `linear-gradient(180deg, ${theme.card} 0%, ${theme.card2} 100%)`,
        boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
        overflow: "hidden",
      },
      cardBody: { padding: 20 },
      productTitle: {
        margin: 0,
        color: theme.text,
        fontSize: 20,
        fontWeight: 900,
        letterSpacing: -0.2,
      },
      productSub: {
        marginTop: 6,
        color: theme.text3,
        fontSize: 12,
        fontWeight: 900,
        textTransform: "uppercase",
        letterSpacing: 0.7,
      },
      productDesc: {
        marginTop: 10,
        color: theme.text2,
        fontSize: 14,
        lineHeight: 1.45,
      },
      label: {
        marginTop: 14,
        marginBottom: 6,
        color: theme.text3,
        fontSize: 12,
        fontWeight: 900,
        textTransform: "uppercase",
        letterSpacing: 0.6,
      },
      listItem: {
        background: "transparent",
        borderColor: "rgba(255,255,255,0.10)",
        paddingLeft: 0,
        paddingRight: 0,
        color: theme.text2,
        fontSize: 13,
      },
      personPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.06)",
        border: `1px solid ${theme.border}`,
        color: theme.text2,
        fontSize: 12,
        fontWeight: 800,
        marginRight: 8,
        marginBottom: 8,
      },
      tierBox: {
        marginTop: 14,
        borderRadius: 18,
        border: `1px solid ${theme.border}`,
        background: "rgba(255,255,255,0.05)",
        padding: 12,
      },
      tierHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 10,
        marginBottom: 10,
      },
      price: { color: theme.text, fontSize: 18, fontWeight: 900 },
      priceMeta: { color: theme.text3, fontSize: 12, fontWeight: 800 },
      ctaRow: {
        marginTop: 14,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        justifyContent: "center",
      },
      btnPrimary: {
        borderRadius: 14,
        padding: "10px 14px",
        fontWeight: 900,
        border: "none",
        background: `linear-gradient(90deg, ${theme.accent} 0%, ${theme.accent2} 100%)`,
        color: "#0b1020",
      },
      btnGhost: {
        borderRadius: 14,
        padding: "10px 14px",
        fontWeight: 900,
        border: `1px solid ${theme.border}`,
        background: "rgba(255,255,255,0.04)",
        color: theme.text,
      },
      sticky: { position: "sticky", top: 18 },
      bundleCard: {
        borderRadius: 20,
        border: `1px solid ${theme.border}`,
        background: `linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)`,
        boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
        overflow: "hidden",
      },
      bundleHeader: {
        padding: 16,
        borderBottom: `1px solid ${theme.border}`,
        background: "rgba(255,255,255,0.04)",
      },
      bundleTitle: {
        margin: 0,
        color: theme.text,
        fontSize: 14,
        fontWeight: 900,
      },
      bundleSub: {
        marginTop: 4,
        color: theme.text3,
        fontSize: 12,
        fontWeight: 800,
      },
      bundleBody: { padding: 14 },
      bundleFooter: {
        padding: 14,
        borderTop: `1px solid ${theme.border}`,
        background: "rgba(255,255,255,0.04)",
      },
    }),
    [theme],
  );

  const productNameById = useMemo(() => {
    const m = {};
    PRODUCTS.forEach((p) => (m[p.id] = p.name));
    return m;
  }, []);

  const defaultTierForProduct = (productId) => tierByProduct[productId] || "basic";

  const filtered = useMemo(() => {
    if (tab === "all") return PRODUCTS;
    if (tab === "monitors") return PRODUCTS.filter((p) => p.group === "monitors");
    if (tab === "cotrader") return PRODUCTS.filter((p) => p.group === "cotrader");
    return PRODUCTS;
  }, [tab]);

  function isInBundle(productId) {
    return bundle.some((x) => x.productId === productId);
  }

  function addToBundle(product) {
    if (isInBundle(product.id)) return;
    const tierKey = product.tiered ? tierByProduct[product.id] || "mid" : undefined;
    setBundle((prev) => [...prev, { productId: product.id, tierKey }]);
  }

  function removeFromBundle(productId) {
    setBundle((prev) => prev.filter((x) => x.productId !== productId));
  }

  function updateBundleTier(productId, tierKey) {
    setBundle((prev) =>
      prev.map((x) => (x.productId === productId ? { ...x, tierKey } : x)),
    );
  }

  // ===== Lead Modal helpers =====
  const openSalesLeadModal = ({ mode, productId } = {}) => {
    setLeadToast({ ok: null, msg: "" });
    setSalesModalMode(mode || "request_pricing");

    if (bundle.length) {
      setSelectedBundleDraft(
        bundle.map((x) => ({ product_id: x.productId, tier_key: x.tierKey })),
      );
    } else if (productId) {
      const prod = PRODUCTS.find((p) => p.id === productId);
      setSelectedBundleDraft([
        {
          product_id: productId,
          tier_key: prod?.tiered ? defaultTierForProduct(productId) : undefined,
        },
      ]);
    } else {
      setSelectedBundleDraft([]);
    }

    setShowSalesModal(true);
  };

  const closeSalesLeadModal = () => {
    setShowSalesModal(false);
    setSubmittingLead(false);
  };

  const addDraftProduct = (productId) => {
    if (!productId) return;
    if (selectedBundleDraft.some((x) => x.product_id === productId)) return;

    const prod = PRODUCTS.find((p) => p.id === productId);
    setSelectedBundleDraft((prev) => [
      ...prev,
      {
        product_id: productId,
        tier_key: prod?.tiered ? defaultTierForProduct(productId) : undefined,
      },
    ]);
  };

  const removeDraftProduct = (productId) => {
    setSelectedBundleDraft((prev) => prev.filter((x) => x.product_id !== productId));
  };

  const updateDraftTier = (productId, tierKey) => {
    setSelectedBundleDraft((prev) =>
      prev.map((x) => (x.product_id === productId ? { ...x, tier_key: tierKey } : x)),
    );
  };

  const submitContactSales = async () => {
    setLeadToast({ ok: null, msg: "" });

    if (!salesForm.full_name.trim())
      return setLeadToast({ ok: false, msg: "Please enter your name." });
    if (!salesForm.work_email.trim())
      return setLeadToast({ ok: false, msg: "Please enter your work email." });
    if (!salesForm.company_name.trim())
      return setLeadToast({ ok: false, msg: "Please enter your company name." });
    if (!selectedBundleDraft.length)
      return setLeadToast({ ok: false, msg: "Please select at least one product." });

    setSubmittingLead(true);
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      const res = await fetch(`${apiUrl}/auth/contact_sales`,{
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          full_name: salesForm.full_name,
          work_email: salesForm.work_email,
          company_name: salesForm.company_name,
          selected_bundle: selectedBundleDraft.map((x) => ({
            product_id: x.product_id,
            tier_key: x.tier_key,
          })),
          message: salesForm.message || "",
        }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.success) {
        const msg =
          data?.errorObject?.message ||
          "Something went wrong while submitting. Please try again.";
        setLeadToast({ ok: false, msg });
        setSubmittingLead(false);
        return;
      }

      setLeadToast({
        ok: true,
        msg:
          data?.results?.message ||
          "Request received. Sales will contact you shortly.",
      });
    } catch (e) {
      setLeadToast({ ok: false, msg: "Network error. Please try again." });
    } finally {
      setSubmittingLead(false);
    }
  };

  function renderTabs() {
    return (
      <div style={styles.tabsWrap}>
        <button style={styles.tabBtn(tab === "all")} onClick={() => setTab("all")}>
          All
        </button>
        <button
          style={styles.tabBtn(tab === "monitors")}
          onClick={() => setTab("monitors")}
        >
          Agents
        </button>
        <button
          style={styles.tabBtn(tab === "cotrader")}
          onClick={() => setTab("cotrader")}
        >
          CoTrader
        </button>
      </div>
    );
  }

  function renderBillingToggle() {
    return (
      <div style={styles.toggleWrap}>
        <button
          style={styles.toggleBtn(billing === "monthly")}
          onClick={() => setBilling("monthly")}
        >
          Monthly
        </button>
      </div>
    );
  }

  function TierPicker({ productId, compact = false }) {
    const current = tierByProduct[productId] || "basic";
    return (
      <ButtonGroup style={{ width: "100%" }}>
        {TIERS.map((t) => (
          <ToggleButton
            key={t.key}
            id={`${productId}_${t.key}`}
            type="radio"
            variant="outline-light"
            name={`${productId}_tier`}
            value={t.key}
            checked={current === t.key}
            onChange={(e) => {
              const next = e.currentTarget.value;
              setTierByProduct((prev) => ({ ...prev, [productId]: next }));
              if (isInBundle(productId)) updateBundleTier(productId, next);
            }}
            style={{
              borderRadius: 14,
              fontWeight: 900,
              fontSize: compact ? 11 : 12,
              padding: compact ? "8px 8px" : "8px 10px",
              borderColor: "rgba(255,255,255,0.18)",
              color: current === t.key ? "#0b1020" : "rgba(255,255,255,0.80)",
              background:
                current === t.key
                  ? "rgba(255,255,255,0.92)"
                  : "rgba(255,255,255,0.04)",
            }}
          >
            {t.label}
          </ToggleButton>
        ))}
      </ButtonGroup>
    );
  }

  function ProductCardUI(product) {
    const tierKey = product.tiered ? tierByProduct[product.id] : null;
    const tier = tierKey ? TIERS.find((t) => t.key === tierKey) : null;

    const shownPrice =
      product.tiered && tier
        ? billing === "monthly"
          ? `${usd(tier.priceMonthly)} / mo`
          : `— / yr`
        : product.salesOnly
          ? "Contact Sales"
          : "";

    return (
      <Card style={styles.card}>
        <Card.Body style={styles.cardBody}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <h3 style={styles.productTitle}>{product.name}</h3>
              <div style={styles.productSub}>{product.subtitle}</div>
            </div>
          </div>

          <p style={styles.productDesc}>{product.description}</p>

          <div style={styles.label}>Core capabilities</div>
          <ListGroup variant="flush">
            {product.capabilities.map((x) => (
              <ListGroup.Item key={x} style={styles.listItem}>
                <span style={{ fontWeight: 900, marginRight: 10, color: "rgba(255,255,255,0.85)" }}>
                  ✓
                </span>
                {x}
              </ListGroup.Item>
            ))}
          </ListGroup>

          <div style={styles.label}>Designed for</div>
          <div>
            {product.designedFor.map((x) => (
              <span key={x} style={styles.personPill}>
                {x}
              </span>
            ))}
          </div>

          {product.tiered ? (
            <div style={styles.tierBox}>
              <div style={styles.tierHeader}>
                <div>
                  <div style={{ color: theme.text, fontSize: 13, fontWeight: 900 }}>
                    Choose a plan
                  </div>
                  <div style={styles.priceMeta}>
                    Discounted plans available for multi-user packages.
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <div style={styles.price}>{shownPrice}</div>
                  <div style={styles.priceMeta}>
                    {billing === "monthly" ? "Billed monthly" : "Billed annually"}
                  </div>
                </div>
              </div>

              <TierPicker productId={product.id} />
            </div>
          ) : (
            <div style={{ marginTop: 12, color: theme.text3, fontSize: 12, fontWeight: 800 }}>
              {product.salesOnly ? "Contact Sales for Price." : "Bundles available."}
            </div>
          )}

          <div style={styles.ctaRow}>
            <Button
              style={styles.btnPrimary}
              disabled={!product.salesOnly && isInBundle(product.id)}
              onClick={() => {
                if (product.salesOnly) {
                  openSalesLeadModal({ mode: "contact_sales", productId: product.id });
                  return;
                }
                addToBundle(product);
              }}
            >
              {product.salesOnly
                ? product.ctaPrimary
                : isInBundle(product.id)
                  ? "Added to Bundle"
                  : "Add to Bundle"}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  function BundlePanel({ isMobile = false }) {
    const items = bundle.map((x) => {
      const product = PRODUCTS.find((p) => p.id === x.productId);
      const tier = x.tierKey ? TIERS.find((t) => t.key === x.tierKey) : null;
      return { ...x, product, tier };
    });

    return (
      <div style={styles.bundleCard}>
        <div style={styles.bundleHeader}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <p style={styles.bundleTitle}>Your Bundle</p>
              <p style={styles.bundleSub}>Build a multi-product configuration (discounts apply)</p>
            </div>
            <Badge bg={items.length ? "primary" : "secondary"} pill>
              {items.length}
            </Badge>
          </div>
        </div>

        <div style={styles.bundleBody}>
          {items.length === 0 ? (
            <div style={{ color: theme.text2, fontSize: 13, fontWeight: 700 }}>
              Add products to start building a bundle.
              <div style={{ marginTop: 10, color: theme.text3, fontSize: 12, fontWeight: 800 }}>
                Tip: CoTrader FX + MacroMonitor includes a 30-day free trial.
              </div>
            </div>
          ) : (
            <Stack gap={2}>
              {items.map((x) => (
                <div
                  key={x.productId}
                  style={{
                    border: `1px solid ${theme.border}`,
                    borderRadius: 16,
                    padding: 12,
                    background: "rgba(255,255,255,0.04)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: theme.text, fontSize: 13, fontWeight: 900 }}>
                        {x.product?.name}
                      </div>
                      <div style={{ color: theme.text3, fontSize: 12, fontWeight: 800 }}>
                        {x.product?.subtitle}
                      </div>
                      {x.tier ? (
                        <div style={{ marginTop: 8 }}>
                          <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
                            {x.tier.label}
                          </Badge>{" "}
                          <span style={{ color: theme.text2, fontSize: 10, fontWeight: 800 }}>
                            {usd(x.tier.priceMonthly)}/mo
                          </span>
                        </div>
                      ) : null}
                    </div>

                    <Button
                      variant="outline-light"
                      size="sm"
                      style={{
                        borderRadius: 12,
                        height: "fit-content",
                        fontWeight: 900,
                        borderColor: "rgba(255,255,255,0.20)",
                      }}
                      onClick={() => removeFromBundle(x.productId)}
                    >
                      Remove
                    </Button>
                  </div>

                  {x.product?.tiered ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ color: theme.text3, fontSize: 12, fontWeight: 900, marginBottom: 6 }}>
                        Tier
                      </div>
                      <TierPicker productId={x.productId} compact />
                    </div>
                  ) : null}
                </div>
              ))}
            </Stack>
          )}
        </div>

        <div style={styles.bundleFooter}>
          <div style={{ color: theme.text3, fontSize: 12, fontWeight: 900 }}>
            Discounted plans available for multi-user packages.
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <Button
              style={{ ...styles.btnPrimary, width: "100%" }}
              disabled={!items.length}
              onClick={() => openSalesLeadModal({ mode: "request_pricing" })}
            >
              Request Pricing
            </Button>
          </div>

          {isMobile ? (
            <div style={{ marginTop: 10, color: theme.text3, fontSize: 12, fontWeight: 800 }}>
              Keep browsing to add more products.
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Container>
        <HeaderComponent isUserLoggedIn={verifyLogin()} />

        {/* HERO */}
        <div style={styles.hero}>
          <Row className="justify-content-between g-3">
            <Col lg={8}>
              <h1 style={styles.title}>Try out for free</h1>
              <div style={styles.subtitle}>
                Try the Macro Monitor and CoTrader FX for a 30 Days free trial.
              </div>

              <div style={styles.chipsRow}>
                <span style={{ ...styles.chip, ...styles.pillAccent }}>
                  ✓ 30-day free trial (CoTrader FX + MacroMonitor)
                </span>
              </div>
            </Col>

            <Col lg={4}>
              <div
                className="justify-content-center align-content-center"
                style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}
              >
                <Button style={styles.btnPrimary} onClick={() => (window.location.href = "/signup")}>
                  Sign up for free
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {/* SECTIONS */}
        <div style={styles.sectionTitle}>Browse products</div>
        <div style={styles.sectionNote}></div>

        <Row className="d-flex mb-4 justify-content-between">
          <Col lg={4} className="d-flex flex-column align-items-lg-start gap-2">
            <div className="d-flex gap-2 flex-wrap justify-content-lg-end">
              {renderTabs()}
              <Button
                className="d-lg-none"
                style={styles.btnPrimary}
                onClick={() => setShowBundleMobile(true)}
              >
                Bundle ({bundle.length})
              </Button>
            </div>
          </Col>

          <Col lg={8} className="d-flex flex-column align-items-lg-end">
            <>
              {renderBillingToggle()}
              <div style={{ color: theme.text3, fontSize: 12, fontWeight: 800 }} />
            </>
          </Col>
        </Row>

        <Row className="g-4 mt-4">
          {/* Main grid */}
          <Col lg={8}>
            <Row className="g-4">
              {filtered.map((p) => (
                <Col key={p.id} xs={12} md={6}>
                  {ProductCardUI(p)}
                </Col>
              ))}
            </Row>
          </Col>

          {/* Sticky bundle */}
          <Col lg={4} className="d-none d-lg-block">
            <div style={styles.sticky}>
              <BundlePanel />
            </div>
          </Col>
        </Row>
      </Container>

      {/* Mobile bundle offcanvas */}
      <Offcanvas
        placement="bottom"
        show={showBundleMobile}
        onHide={() => setShowBundleMobile(false)}
        style={{
          height: "88vh",
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          background: "transparent",
        }}
      >
        <Offcanvas.Header
          closeButton
          style={{
            background: "rgba(11,16,32,0.96)",
            borderBottom: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          <Offcanvas.Title style={{ color: "rgba(255,255,255,0.92)", fontWeight: 900 }}>
            Your Bundle
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ background: "rgba(11,16,32,0.96)" }}>
          <BundlePanel isMobile />
        </Offcanvas.Body>
      </Offcanvas>

      {/* Sales / Pricing Lead Modal */}
      <Modal show={showSalesModal} onHide={closeSalesLeadModal} centered contentClassName="border-0" style={{
        
      }}>
        <div
          style={{
            border: `1px solid ${theme.border}`,
            background: "rgba(11,16,32,0.98)",
            boxShadow: theme.shadow,
            overflow: "hidden",
          }}
        >
          <Modal.Header
            closeButton
            style={{
              borderBottom: `1px solid ${theme.border}`,
              background: "rgba(255,255,255,0.03)",
            }}
          >
            <Modal.Title style={{ color: theme.text, fontWeight: 900 }}>
              {salesModalMode === "contact_sales" ? "Contact Sales" : "Request Pricing"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body style={{ padding: 16 }}>
            {/* Products selector */}
            <div
              style={{
                border: `1px solid ${theme.border}`,
                borderRadius: 14,
                padding: 12,
                background: "rgba(255,255,255,0.04)",
              }}
            >
              <div style={{ color: theme.text3, fontSize: 12, fontWeight: 900, marginBottom: 8 }}>
                Selected products
              </div>

              <div className="d-flex gap-2 align-items-center flex-wrap">
                <Form.Select
                  size="sm"
                  style={{
                    maxWidth: 260,
                    background: "rgba(255,255,255,0.06)",
                    color: theme.text,
                    borderColor: "rgba(255,255,255,0.18)",
                    fontWeight: 800,
                  }}
                  value=""
                  onChange={(e) => addDraftProduct(e.target.value)}
                >
                  <option value="" style={{ color: "#0b1020" }}>
                    + Add a product…
                  </option>
                  {PRODUCTS.map((p) => (
                    <option key={p.id} value={p.id} style={{ color: "#0b1020" }}>
                      {p.name}
                    </option>
                  ))}
                </Form.Select>

                <div style={{ color: theme.text3, fontSize: 12, fontWeight: 800 }}>
                  (prefilled from your bundle)
                </div>
              </div>

              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                {selectedBundleDraft.length === 0 ? (
                  <div style={{ color: theme.text2, fontSize: 13, fontWeight: 700 }}>
                    No products selected yet.
                  </div>
                ) : (
                  selectedBundleDraft.map((x) => {
                    const prod = PRODUCTS.find((p) => p.id === x.product_id);
                    const isTiered = !!prod?.tiered;

                    return (
                      <div
                        key={x.product_id}
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                          justifyContent: "space-between",
                          border: `1px solid ${theme.border}`,
                          borderRadius: 12,
                          padding: 10,
                          background: "rgba(255,255,255,0.03)",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: theme.text, fontWeight: 900, fontSize: 13 }}>
                            {productNameById[x.product_id] || x.product_id}
                          </div>
                          <div style={{ color: theme.text3, fontWeight: 800, fontSize: 12 }}>
                            {prod?.subtitle}
                          </div>

                          {isTiered ? (
                            <div style={{ marginTop: 8 }}>
                              <ButtonGroup size="sm">
                                {TIERS.map((t) => (
                                  <Button
                                    key={t.key}
                                    variant="outline-light"
                                    onClick={() => updateDraftTier(x.product_id, t.key)}
                                    style={{
                                      fontWeight: 900,
                                      borderColor: "rgba(255,255,255,0.18)",
                                      background:
                                        x.tier_key === t.key
                                          ? "rgba(255,255,255,0.92)"
                                          : "rgba(255,255,255,0.04)",
                                      color:
                                        x.tier_key === t.key ? "#0b1020" : "rgba(255,255,255,0.85)",
                                      borderRadius: 10,
                                    }}
                                  >
                                    {t.label}
                                  </Button>
                                ))}
                              </ButtonGroup>
                            </div>
                          ) : (
                            <div style={{ marginTop: 8, color: theme.text3, fontSize: 12, fontWeight: 800 }}>
                              Sales-only product
                            </div>
                          )}
                        </div>

                        <Button
                          size="sm"
                          variant="outline-light"
                          onClick={() => removeDraftProduct(x.product_id)}
                          style={{
                            borderRadius: 10,
                            fontWeight: 900,
                            borderColor: "rgba(255,255,255,0.18)",
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Lead form */}
            <div style={{ marginTop: 14 }}>
              <Form>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label style={{ color: theme.text3, fontSize: 12, fontWeight: 900 }}>
                      Full name
                    </Form.Label>
                    <Form.Control
                      value={salesForm.full_name}
                      onChange={(e) =>
                        setSalesForm((p) => ({ ...p, full_name: e.target.value }))
                      }
                      placeholder="Your name"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        borderColor: "rgba(255,255,255,0.18)",
                        color: theme.text,
                        fontWeight: 800,
                        borderRadius: 12,
                      }}
                    />
                  </Col>

                  <Col md={6}>
                    <Form.Label style={{ color: theme.text3, fontSize: 12, fontWeight: 900 }}>
                      Work email
                    </Form.Label>
                    <Form.Control
                      value={salesForm.work_email}
                      onChange={(e) =>
                        setSalesForm((p) => ({ ...p, work_email: e.target.value }))
                      }
                      placeholder="name@company.com"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        borderColor: "rgba(255,255,255,0.18)",
                        color: theme.text,
                        fontWeight: 800,
                        borderRadius: 12,
                      }}
                    />
                  </Col>

                  <Col md={12}>
                    <Form.Label style={{ color: theme.text3, fontSize: 12, fontWeight: 900 }}>
                      Company name
                    </Form.Label>
                    <Form.Control
                      value={salesForm.company_name}
                      onChange={(e) =>
                        setSalesForm((p) => ({ ...p, company_name: e.target.value }))
                      }
                      placeholder="Company"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        borderColor: "rgba(255,255,255,0.18)",
                        color: theme.text,
                        fontWeight: 800,
                        borderRadius: 12,
                      }}
                    />
                  </Col>

                  <Col md={12}>
                    <Form.Label style={{ color: theme.text3, fontSize: 12, fontWeight: 900 }}>
                      Message (optional)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={salesForm.message}
                      onChange={(e) =>
                        setSalesForm((p) => ({ ...p, message: e.target.value }))
                      }
                      placeholder="Anything we should know? (users, desks, integrations...)"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        borderColor: "rgba(255,255,255,0.18)",
                        color: theme.text,
                        fontWeight: 800,
                        borderRadius: 12,
                      }}
                    />
                  </Col>
                </Row>
              </Form>
            </div>

            {/* Status message */}
            {leadToast.ok !== null ? (
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: `1px solid ${theme.border}`,
                  background: leadToast.ok
                    ? "rgba(52,211,153,0.14)"
                    : "rgba(251,191,36,0.14)",
                  color: theme.text,
                  fontWeight: 900,
                  fontSize: 13,
                }}
              >
                {leadToast.msg}
              </div>
            ) : null}
          </Modal.Body>

          <Modal.Footer
            style={{
              borderTop: `1px solid ${theme.border}`,
              background: "rgba(255,255,255,0.03)",
              padding: 12,
            }}
          >
            <Button
              variant="outline-light"
              onClick={closeSalesLeadModal}
              style={{
                borderRadius: 12,
                fontWeight: 900,
                borderColor: "rgba(255,255,255,0.18)",
              }}
              disabled={submittingLead}
            >
              Cancel
            </Button>

            <Button
              style={{ ...styles.btnPrimary, borderRadius: 12 }}
              onClick={submitContactSales}
              disabled={submittingLead || leadToast.ok }
            >
              {submittingLead ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Submitting…
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
}
